/**
 * WebProfileMenu — the web app's profile disclosure, composed onto the
 * shared `ProfileMenu` primitive.
 *
 * The dark-mode toggle lives HERE (per PRD) as the first menu item,
 * wired to `useTheme().toggle`. v35 renders the toggle as a separate
 * header button; the validated design + PRD consolidate it into the
 * profile menu so the header stays a clean brand/avatar pair and the
 * mode control is discoverable next to the user's other preferences.
 *
 * Items mirror v35 `.profile-dropdown`: theme toggle, a dimmed
 * "Settings" placeholder (v35 `.dimmed` — not wired yet), a divider,
 * then a destructive "Log out" (v35 `.danger`).
 */
import { Moon, Sun, Settings, LogOut } from "lucide-react";
import { ProfileMenu } from "@nu-assist/ui";
import { useTheme } from "@nu-assist/shared-fe";

export interface WebProfileMenuProps {
  /** Display name (dropdown header). */
  name: string;
  /** Email (dropdown header). */
  email: string;
  /** Optional role pill. */
  role?: string;
  /** Logout handler. */
  onLogout?: () => void;
}

/** "Jared Cohen" → "JC"; falls back to the first 2 chars of one token. */
function initialsOf(name: string): string {
  const tokens = name.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return "··";
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return (tokens[0][0] + tokens[tokens.length - 1][0]).toUpperCase();
}

export function WebProfileMenu({
  name,
  email,
  role,
  onLogout,
}: WebProfileMenuProps) {
  const { theme, toggle } = useTheme();
  const nextLabel = theme === "dark" ? "Light mode" : "Dark mode";

  return (
    <ProfileMenu
      initials={initialsOf(name)}
      name={name}
      email={email}
      role={role}
      items={[
        {
          label: nextLabel,
          icon:
            theme === "dark" ? (
              <Sun size={14} aria-hidden="true" />
            ) : (
              <Moon size={14} aria-hidden="true" />
            ),
          onSelect: toggle,
        },
        {
          label: "Settings",
          icon: <Settings size={14} aria-hidden="true" />,
          disabled: true,
        },
        null,
        {
          label: "Log out",
          icon: <LogOut size={14} aria-hidden="true" />,
          danger: true,
          onSelect: onLogout,
        },
      ]}
    />
  );
}
